import React, { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { useAppSelector } from 'store/hooks';
import { AppView, Button, ButtonGroup, CardView, rem, SCROLL_INDICATOR_INSETS, withAnimatedNavigationHeader, } from 'design-system';
import { HEIGHT } from 'design-system/Button';
import IconTooltip from 'design-system/icons/IconTooltip';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import StatusBar from 'design-system/StatusBar';
import Text from 'design-system/Text';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { muteErrorAndResult, useFetchOld } from 'hooks/useFetch';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import { reverseTrialDeleteFunnelStart, } from 'features/premium/actions/reverseTrial';
import { tierThemes } from 'features/premium/benefits';
import { selectSubscriptionPrices } from 'features/premium/selectors';
import createStyleSheets from 'utils/createStyleSheets';
import FastImage from 'utils/packages/rnFastImage';
import useNavigateToFinalDeleteStep from './hooks/useNavigateToFinalDeleteStep';
const strings = {
    iAmNotInterested: 'I am not interested',
    greatLetMeExplore: 'Great, let me explore',
    noCharge: 'No charge',
    noAutomaticPayment: 'There is no automatic payment at the end',
    feelFreeTeExplore: 'Feel free to explore all the features Emma has to offer for 30 days with no charge at all.',
};
const DeleteReverseTrialStartScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const [, , isFetching, fetch] = useFetchOld(muteErrorAndResult);
    const onPressStart = useCallback(async () => {
        const result = await fetch(reverseTrialDeleteFunnelStart());
        if (result) {
            navigation.navigateDeprecated('DeleteReverseTrialAcknowledge', route.params);
        }
    }, [route.params]);
    const onPressDecline = useNavigateToFinalDeleteStep();
    const prices = useAppSelector(selectSubscriptionPrices);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT * 2, paddingHorizontalStyle);
    const tier = route.params.reverseTrialTier;
    const title = `We’ve upgraded your account for free to ${tierThemes[tier].name}!`;
    const styles = useStyles(styleSet);
    return (<AppView withFrame={false}>
          <Animated.ScrollView scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} onScroll={scrollHandler} contentContainerStyle={paddingBottom}>
            <StatusBar barStyle="dark-content" animated/>
            <ScreenInfoHeader title={title} subTitle={`${tierThemes[tier].name} has all the best features and it costs ${prices?.YEARLY[tier]?.localizedPrice}/yr, but we have decided to give you everything for FREE with no charge at all for 30 days!`} smallSubTitle/>
            <View style={styles.giftCardContainer}>
              <CardView noMarginTop style={styles.giftCard}/>
              <View style={styles.giftContainerOuter}>
                <View style={styles.giftContainer}>
                  <FastImage style={staticStyles.giftImage} source={require('features/premium/img/gift.png')}/>
                  <View style={styles.giftChip}>
                    <Text Text-14 White>
                      A gift for you!
                    </Text>
                  </View>
                </View>
              </View>
            </View>
            <CardView noMarginTop noMarginBottom withPadding>
              <View style={styles.infoCardHeader}>
                <IconTooltip colorKey="brand" style={styles.infoIcon}/>
                <Text Text-14 Primary>
                  {strings.noCharge}
                </Text>
              </View>
              <Text Text-16 OffBlack style={styles.infoCardHeader}>
                {strings.noAutomaticPayment}
              </Text>
              <Text TextThin-14 Secondary>
                {strings.feelFreeTeExplore}
              </Text>
            </CardView>
          </Animated.ScrollView>
          <ButtonGroup type="floating">
            <Button title={strings.iAmNotInterested} brandReversed onPress={onPressDecline} testID="deleteButton"/>
            <Button title={strings.greatLetMeExplore} brand onPress={onPressStart} isFetching={isFetching} testID="startButton"/>
          </ButtonGroup>
        </AppView>);
}, ({ route, colors }) => ({
    title: `Upgraded to ${tierThemes[route.params.reverseTrialTier].shortName}!`,
    hideTitleOnScroll: true,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const staticStyles = StyleSheet.create({
    giftImage: {
        width: rem(107),
        height: rem(124),
        position: 'absolute',
        bottom: rem(10),
    },
});
const styleSet = createStyleSheets((colors) => ({
    giftCardContainer: {
        justifyContent: 'flex-end',
        height: rem(155),
        marginTop: rem(24),
    },
    giftCard: {
        height: rem(111),
    },
    giftContainer: {
        position: 'absolute',
        top: 0,
        alignItems: 'center',
        bottom: rem(44),
    },
    giftContainerOuter: {
        ...StyleSheet.absoluteFillObject,
        alignItems: 'center',
    },
    giftChip: {
        position: 'absolute',
        bottom: 0,
        backgroundColor: colors.background.contrast,
        borderRadius: rem(9),
        paddingVertical: rem(7),
        paddingHorizontal: rem(12),
    },
    infoIcon: {
        marginRight: rem(4),
    },
    infoCardHeader: {
        flexDirection: 'row',
        marginBottom: rem(4),
    },
}));
export default DeleteReverseTrialStartScreen;
